import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

interface CalendlyWindow extends Window {
  Calendly?: {
    initPopupWidget: (options: { url: string }) => void;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CalendlyService {
  private readonly widgetCssUrl = 'https://assets.calendly.com/assets/external/widget.css';
  private readonly widgetScriptUrl = 'https://assets.calendly.com/assets/external/widget.js';
  private scriptLoadPromise?: Promise<void>;

  constructor(@Inject(DOCUMENT) private readonly doc: Document) { }

  openPopup(url: string): false {
    const win = this.doc.defaultView as CalendlyWindow | null;
    if (!win) {
      return false;
    }

    this.loadWidget(win)
      .then(() => win.Calendly?.initPopupWidget({ url }))
      .catch(() => undefined);

    return false;
  }

  private loadWidget(win: CalendlyWindow): Promise<void> {
    this.ensureStylesheet();

    if (win.Calendly?.initPopupWidget) {
      return Promise.resolve();
    }

    if (!this.scriptLoadPromise) {
      this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
        const existingScript = this.doc.querySelector<HTMLScriptElement>(`script[src="${this.widgetScriptUrl}"]`);
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(), { once: true });
          existingScript.addEventListener('error', () => reject(), { once: true });
          return;
        }

        const script = this.doc.createElement('script');
        script.src = this.widgetScriptUrl;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject();
        this.doc.body.appendChild(script);
      });
    }

    return this.scriptLoadPromise;
  }

  private ensureStylesheet(): void {
    if (this.doc.querySelector(`link[href="${this.widgetCssUrl}"]`)) {
      return;
    }

    const link = this.doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = this.widgetCssUrl;
    this.doc.head.appendChild(link);
  }
}
